import React from "react";
import Section from "@/components/foundation/Section";
import FadeIn from "@/components/foundation/FadeIn";
import { AutoLinkPartners } from "@/components/marketing/PartnerLink";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "What is ZenScore?",
    a: "ZenScore is ZenMe's proprietary funding intelligence signal. It analyzes live business banking activity — cash flow, deposits, balance patterns, revenue trends, and stability — to reveal how ready your business is for funding today. It is not a personal credit score and does not require credit-bureau access to compute.",
  },
  {
    q: "How is ZenScore calculated?",
    a: "ZenScore is generated by an AI underwriting model built with our partner Uptiq AI. It ingests structured banking data provided through your Mastercard Finicity-powered connection, extracts financial signals, and produces a normalized readiness score alongside the underlying metrics you can review yourself.",
  },
  {
    q: "Will connecting my bank affect my credit?",
    a: "No. Connecting your bank through Mastercard Finicity is a read-only data connection. It does not perform a credit inquiry and has no effect on your personal or business credit profile.",
  },
  {
    q: "Is my banking information secure?",
    a: "Yes. Bank connectivity is facilitated by Mastercard Finicity — a leading open-banking provider used by major financial institutions. Your data is encrypted in transit and at rest with AES-256, and ZenMe never stores or requests your online banking credentials.",
  },
  {
    q: "Who powers bank connectivity?",
    a: "Mastercard Finicity handles all bank connections for ZenMe. Finicity supports thousands of financial institutions across the United States and Canada and is trusted by leading lenders, banks, and fintech platforms.",
  },
  {
    q: "Why does ZenMe use Mastercard Finicity?",
    a: "Because it is one of the most secure and reliable open-banking rails available. Finicity's coverage, uptime, and data quality allow ZenMe to focus on the intelligence layer while relying on best-in-class infrastructure for the connection itself.",
  },
  {
    q: "What is Uptiq AI?",
    a: "Uptiq AI is our AI infrastructure partner. It powers the models and pipelines that translate raw banking data into the underwriting signals behind ZenScore and the funding readiness insights you see in the app.",
  },
  {
    q: "How long does funding take?",
    a: "It varies by business profile and funding partner. Because ZenMe pre-verifies your financial data and pre-fills your application, many businesses complete underwriting significantly faster than traditional processes. ZenMe does not guarantee approval or specific turnaround times.",
  },
  {
    q: "Who qualifies?",
    a: "ZenMe is built for operating businesses with a business bank account and consistent revenue activity. Funding availability depends on the partner network, your ZenScore, and the specific product being applied for.",
  },
  {
    q: "How much funding can I receive?",
    a: "Funding ranges are personalized to your ZenScore, business tenure, revenue, and available partner offers. Ranges shown in the app are examples of potential offers you may qualify for and are not guarantees.",
  },
  {
    q: "Can I disconnect my bank at any time?",
    a: "Yes. You can revoke your bank connection at any time from your ZenMe settings or directly through your Finicity data-sharing dashboard. Disconnecting stops future data updates but may affect your ability to receive live funding matches.",
  },
  {
    q: "How is ZenMe different from a traditional lender?",
    a: "ZenMe is not a direct lender. We build the intelligence layer — ZenScore, banking analytics, and funding readiness insights — and connect qualified businesses to a network of funding partners. Our focus is helping you understand your funding potential and matching you with relevant options, not underwriting the capital ourselves.",
  },
];

export const FaqSection = () => {
  return (
    <Section
      eyebrow="FAQ"
      headline="Common questions."
      subheadline="Straight answers about ZenScore, how bank connections work, and where ZenMe fits in the funding ecosystem."
      spacing="lg"
    >
      <FadeIn>
        <Accordion
          type="single"
          collapsible
          className="mx-auto w-full max-w-3xl divide-y divide-white/[0.06] overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.01]"
          data-testid="faq-accordion"
        >
          {FAQS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`faq-${i}`}
              className="border-b-0 px-6 first:pt-2 last:pb-2"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="py-5 text-left text-base font-medium text-white hover:no-underline data-[state=open]:text-brand-emerald sm:text-lg">
                <AutoLinkPartners text={item.q} />
              </AccordionTrigger>
              <AccordionContent className="pb-5 pr-8 text-sm leading-relaxed text-text-secondary">
                <AutoLinkPartners text={item.a} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </FadeIn>
    </Section>
  );
};

export default FaqSection;
